import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQueryWithAuth } from "~/ui-rtk/utils/base-query"

export interface DroppedFile {
  fileName: string
  size: number
  uploadedAt: string
}

export interface FileDropUploadResponse {
  status: "success" | "error"
  result: {
    file: string
    status: "success" | "error"
    message?: string
  }[]
}

interface PresignFileDropRequest {
  fileName: string
  contentType?: string
  size: number
}

interface PresignFileDropResponse {
  uploadUrl: string
  key: string
  contentType: string
  expiresIn: number
}

const errorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null) {
    const data = (error as { data?: unknown }).data
    if (typeof data === "object" && data !== null && "message" in data) {
      const message = (data as { message: unknown }).message
      return Array.isArray(message) ? message.join(", ") : String(message)
    }
    if ("status" in error) {
      return `Request failed (${String((error as { status: unknown }).status)})`
    }
  }
  return error instanceof Error ? error.message : "Upload failed"
}

export const fileDropApi = createApi({
  reducerPath: "fileDropApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["DroppedFiles"],
  endpoints: builder => ({
    getDroppedFiles: builder.query<DroppedFile[], void>({
      query: () => ({
        url: "/file-drop",
      }),
      providesTags: ["DroppedFiles"],
    }),

    /**
     * Presign + direct S3 PUT per file. The old multipart `POST /file-drop`
     * goes through API Gateway, which returns 413 for bodies over 10 MB
     * (surfacing in the browser as a CORS error, since the gateway's error
     * response carries no CORS headers). Only the small presign JSON request
     * crosses the gateway here; the bytes go browser → S3.
     */
    dropFiles: builder.mutation<FileDropUploadResponse, File[]>({
      queryFn: async (files, _api, _extra, fetchWithBQ) => {
        const result: FileDropUploadResponse["result"] = []

        for (const file of files) {
          try {
            const body: PresignFileDropRequest = {
              fileName: file.name,
              size: file.size,
              ...(file.type && { contentType: file.type }),
            }
            const presign = await fetchWithBQ({
              url: "/file-drop/presign",
              method: "POST",
              body,
            })
            if (presign.error) {
              throw presign.error
            }
            const { uploadUrl, contentType } =
              presign.data as PresignFileDropResponse

            // Content-Type MUST match the type the URL was signed with, or
            // S3 rejects the PUT with 403.
            const put = await fetch(uploadUrl, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": contentType },
            })
            if (!put.ok) {
              throw new Error(`S3 upload failed (${put.status})`)
            }

            result.push({ file: file.name, status: "success" })
          } catch (error) {
            result.push({
              file: file.name,
              status: "error",
              message: errorMessage(error),
            })
          }
        }

        return {
          data: {
            status: result.some(file => file.status === "error")
              ? "error"
              : "success",
            result,
          },
        }
      },
      invalidatesTags: ["DroppedFiles"],
    }),
  }),
})

export const { useGetDroppedFilesQuery, useDropFilesMutation } = fileDropApi
